import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../theme';

/**
 * Renders a read-only DataGrid's columns + rows as a stacked card list for
 * mobile. The first (or specified) column becomes each card's title; the rest
 * render as label/value pairs. Reuses the grid's existing `renderCell` so the
 * displayed values match the desktop table exactly.
 */
export function ReadOnlyCardList({
  columns,
  rows,
  titleField,
  getRowId,
}: {
  columns: any[];
  rows: any[];
  titleField?: string;
  getRowId?: (row: any, index: number) => string | number;
}) {
  const visible = columns.filter((c) => c && c.field && c.field !== 'delete' && c.field !== '__check__' && c.field !== 'actions');
  const titleCol = titleField ? columns.find((c) => c.field === titleField) : visible[0];
  const bodyCols = visible.filter((c) => c !== titleCol);

  const cell = (col: any, row: any, rowId: any): React.ReactNode => {
    if (!col) return '—';
    const raw = row?.[col.field];
    if (col.renderCell) {
      try {
        const out = col.renderCell({ value: raw, row, id: rowId, field: col.field, colDef: col, api: {} });
        if (out !== undefined && out !== null) return out;
      } catch {
        /* fall through to raw */
      }
    }
    if (col.valueGetter) {
      try {
        const v = col.valueGetter(raw, row, col, {});
        if (v !== undefined && v !== null && v !== '') return typeof v === 'number' ? v.toLocaleString() : v;
      } catch {
        /* fall through */
      }
    }
    if (raw === undefined || raw === null || raw === '') return '—';
    return typeof raw === 'number' ? raw.toLocaleString() : raw;
  };

  if (!rows || rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: colors.grey[600], py: 2, textAlign: 'center' }}>
        No items.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map((row, idx) => {
        const rowId = getRowId ? getRowId(row, idx) : (row?.id ?? idx);
        return (
          <Box key={rowId} sx={{ border: `1px solid ${colors.grey[300]}`, borderRadius: 2, p: 2, bgcolor: '#fff' }}>
            {titleCol && (
              <Typography sx={{ fontWeight: 700, color: colors.grey[900], mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {cell(titleCol, row, rowId)}
              </Typography>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
              {bodyCols.map((col) => (
                <Box key={col.field} sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: colors.grey[600], fontSize: '0.62rem', display: 'block' }}>
                    {col.headerName}
                  </Typography>
                  <Typography component="div" sx={{ fontWeight: 600, color: colors.grey[900], fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cell(col, row, rowId)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default ReadOnlyCardList;
